import React from 'react';
import {
  ArrowRight,
  Bot,
  ClipboardCheck,
  FileSearch,
  FileText,
  Mic,
  UserCheck,
  Users,
  Video,
} from 'lucide-react';
import { Button } from '@/Components/ui/button';
import Header from '../Header&Footer/Header';
import Footer from '../Header&Footer/Footer';
import useSEO from '../HomePage/useSEO';
import ScrollReveal from '../HomePage/ScrollReveal';
import {
  IndustryCta,
  IndustryFlowStyles,
  IndustryWorkflow,
  Section,
  SectionIntro,
} from '../Industries/industryUi';

const SIGNUP_PATH = '/hirewithus/signup';
const INTERVIEW_DEMO_PATH = '/hirewithus/demo';

const WORKFLOW = [
  { title: 'JD', detail: 'Start with the role details and the criteria your team needs to assess.', icon: FileText },
  { title: 'Resume', detail: 'Add the candidate resume to keep the interview grounded in the candidate context.', icon: FileSearch },
  { title: 'AI generates interview', detail: 'Generate questions from the job description and role requirements.', icon: Bot },
  { title: 'Candidate enters interview', detail: 'The candidate joins the AI interview in the browser.', icon: Video },
  { title: 'AI asks role-specific questions', detail: 'The interviewer follows questions relevant to the role, not a generic script.', icon: Mic },
  { title: 'Candidate responds', detail: 'Capture the candidate responses during the structured first-round interview.', icon: Users },
  { title: 'Transcript + recording', detail: 'Keep the transcript and, when recording is enabled, the interview recording for review.', icon: FileText },
  { title: 'AI evaluation', detail: 'Create structured evaluation evidence from the interview responses.', icon: ClipboardCheck },
  { title: 'Recruiter receives report', detail: 'Give the recruiter the evidence they need to review before the next decision.', icon: UserCheck },
];

const REVIEW_EVIDENCE = [
  {
    title: 'Candidate responses',
    detail: 'Review what the candidate said in the first-round conversation.',
    icon: Mic,
  },
  {
    title: 'Transcript and recording',
    detail: 'Keep the interview transcript and, where enabled, recording evidence together.',
    icon: FileText,
  },
  {
    title: 'Structured evaluation',
    detail: 'Use the role-aligned evaluation to focus the recruiter review.',
    icon: ClipboardCheck,
  },
  {
    title: 'Recruiter report',
    detail: 'Give the hiring team a report to review before deciding who moves forward.',
    icon: UserCheck,
  },
];

export default function VirtualHiring() {
  useSEO({
    title: 'AI Interview-as-a-Service | SearchJob.ai',
    description:
      'Automate first-round interviews with role-specific AI video and voice interviews, transcripts and structured evaluation.',
    canonical: 'https://searchjob.ai/hirewithus/virtualhiring',
    ogImage: 'https://searchjob.ai/og-image.png',
  });

  return (
    <div className="min-h-screen overflow-x-hidden bg-background">
      <Header />
      <main className="pt-16">
        <Hero />
        <ScrollReveal>
          <RoleSpecificInterviewing />
        </ScrollReveal>
        <ScrollReveal>
          <InterviewWorkflow />
        </ScrollReveal>
        <ScrollReveal>
          <ReviewEvidence />
        </ScrollReveal>
        <FinalCta />
      </main>
      <Footer />
      <IndustryFlowStyles />
    </div>
  );
}

function Hero() {
  return (
    <section className="relative isolate overflow-hidden bg-background">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute right-0 top-0 h-64 w-64 rounded-full bg-primary/5 blur-3xl" />
      </div>
      <div className="mx-auto grid max-w-7xl items-start gap-12 px-4 pt-12 pb-16 sm:px-6 lg:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)] lg:gap-14 lg:px-8 lg:pt-16">
        <div className="text-center lg:text-left">
          <p className="mb-5 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary">
            <Bot className="h-3.5 w-3.5" aria-hidden="true" />
            AI interviews
          </p>
          <h1 className="mb-6 text-4xl font-bold leading-[1.06] tracking-[-0.02em] text-foreground sm:text-5xl lg:text-[3.4rem] xl:text-6xl">
            Stop Spending Recruiter Hours on <span className="bg-gradient-to-r from-primary via-emerald-600 to-teal-600 bg-clip-text text-transparent">First-Round Interviews.</span>
          </h1>
          <p className="mx-auto mb-8 max-w-xl text-lg leading-relaxed text-muted-foreground sm:text-xl lg:mx-0">
            Let AI conduct them. SearchJob creates role-specific AI interviews and captures responses, transcripts and structured evaluation for recruiter review.
          </p>
          <div className="flex flex-col justify-center gap-3 sm:flex-row lg:justify-start">
            <Button
              asChild
              size="lg"
              className="!h-12 !rounded-full !bg-primary px-8 text-base font-semibold !text-primary-foreground shadow-lg shadow-primary/25 group hover:!bg-primary/90"
            >
              <a href={SIGNUP_PATH}>
                Run Your First AI Interview
                <ArrowRight className="ml-1.5 h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
              </a>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="!h-12 !rounded-full bg-card/70 px-8 text-base font-semibold backdrop-blur-md"
            >
              <a href={INTERVIEW_DEMO_PATH}>See AI Interview Demo</a>
            </Button>
          </div>
        </div>
        <LiveDemoCard />
      </div>
    </section>
  );
}

function LiveDemoCard() {
  return (
    <aside className="relative self-start rounded-3xl border border-primary bg-primary/5 p-5 sm:p-6 lg:p-8" aria-label="AI interview demo">
      <div className="pointer-events-none absolute -inset-4 -z-10 rounded-[3rem] bg-gradient-to-tr from-primary/20 via-emerald-500/10 to-teal-500/20 blur-3xl" />
      <div className="flex items-start gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-primary/30 bg-primary/10 text-primary">
          <Video className="h-5 w-5" aria-hidden="true" />
        </span>
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Live interactive demo</p>
          <h2 className="mt-1 text-xl font-semibold tracking-tight text-foreground">Try an actual AI interview.</h2>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
            The existing demo lets you start a real AI interview and see its transcript and evaluation flow.
          </p>
        </div>
      </div>
      <ul className="mt-5 space-y-2">
        {['AI interviewer', 'Live transcript', 'AI evaluation of responses'].map((item) => (
          <li key={item} className="flex items-center rounded-xl border border-primary/20 bg-background px-3 py-2.5 text-sm text-foreground">
            {item}
          </li>
        ))}
      </ul>
      <a
        href={INTERVIEW_DEMO_PATH}
        className="group mt-5 inline-flex min-h-11 items-center gap-1.5 text-sm font-semibold text-primary transition-colors hover:text-primary/80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
      >
        Open the live demo
        <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 motion-reduce:transition-none motion-reduce:group-hover:translate-x-0" aria-hidden="true" />
      </a>
    </aside>
  );
}

function RoleSpecificInterviewing() {
  return (
    <Section muted>
      <SectionIntro
        eyebrow="Role-specific interviewing"
        title="Role-specific interviewing, not generic scripts."
        copy="Start from the job description and candidate context so the first conversation is relevant to the role your team is hiring for."
      />
      <div className="grid gap-4 md:grid-cols-2">
        <FocusCard
          title="AI handles the structured first round"
          items={['Role-aligned interview questions', 'Candidate responses', 'Transcript and structured evaluation']}
        />
        <FocusCard
          title="Your hiring team stays in control"
          items={['Recruiter review', 'Context and judgment', 'Final hiring decision']}
        />
      </div>
      <p className="mt-5 max-w-3xl text-base leading-relaxed text-muted-foreground">
        AI handles structured first-round evaluation. The hiring team makes the final decision.
      </p>
    </Section>
  );
}

function FocusCard({ title, items }) {
  return (
    <article className="rounded-2xl border border-primary bg-primary/5 p-5">
      <h3 className="text-base font-semibold text-foreground">{title}</h3>
      <ul className="mt-5 space-y-3">
        {items.map((item) => (
          <li key={item} className="flex items-start gap-3 text-sm leading-relaxed text-foreground">
            <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary/70" aria-hidden="true" />
            {item}
          </li>
        ))}
      </ul>
    </article>
  );
}

function InterviewWorkflow() {
  return (
    <Section id="interview-workflow">
      <SectionIntro
        eyebrow="How it works"
        title="A structured first-round interview, from role to recruiter report."
        copy="Each step captures evidence for recruiter review instead of asking the team to rely on a generic interview script."
      />
      <IndustryWorkflow steps={WORKFLOW} uniformCards />
    </Section>
  );
}

function ReviewEvidence() {
  return (
    <Section muted>
      <SectionIntro
        eyebrow="Recruiter review"
        title="Review the interview, not just a score."
        copy="Use the interview evidence to understand the candidate before deciding whether to progress them."
      />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {REVIEW_EVIDENCE.map((item) => (
          <article key={item.title} className="rounded-2xl border border-primary bg-primary/5 p-5">
            <span className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl border border-primary/30 bg-primary/10 text-primary">
              <item.icon className="h-5 w-5" aria-hidden="true" />
            </span>
            <h3 className="text-base font-semibold text-foreground">{item.title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{item.detail}</p>
          </article>
        ))}
      </div>
    </Section>
  );
}

function FinalCta() {
  return (
    <IndustryCta
      title="Make first-round interviews easier to review."
      copy="Run role-specific AI interviews, then give your team the evidence to make the next decision."
      primaryLabel="Run Your First AI Interview"
      primaryHref={SIGNUP_PATH}
      secondaryLabel="See AI Interview Demo"
      secondaryHref={INTERVIEW_DEMO_PATH}
    />
  );
}
